import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WHATSAPP_LINK_NR1 } from '@/constants/whatsapp';
import { cn } from '@/lib/utils';

const LOGO_CTA_LIGHT =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';

const CERT_ROWS: ReadonlyArray<{ lei: ReactNode; plumaa: ReactNode }> = [
	{
		lei: (
			<>
				<strong>Identificar fatores psicossociais</strong> no trabalho
				(sobrecarga, conflito, assédio, falta de autonomia).
			</>
		),
		plumaa: (
			<>
				Questionário científico aplicado <strong>todo mês</strong> a
				100% da equipe.
			</>
		),
	},
	{
		lei: (
			<>
				<strong>Avaliar e documentar</strong> os riscos em laudo anexado
				ao programa oficial da empresa (PGR).
			</>
		),
		plumaa: (
			<>
				Laudo completo, <strong>assinado por médica com CRM</strong>,
				pronto pra anexar ao PGR.
			</>
		),
	},
	{
		lei: (
			<>
				<strong>Plano de ação documentado</strong> com medidas para
				mitigar os fatores identificados.
			</>
		),
		plumaa: (
			<>
				<strong>Devolutiva editorial + 1 palestra/mês</strong> escolhida
				pelos dados reais.
			</>
		),
	},
	{
		lei: (
			<>
				<strong>Histórico periódico</strong> demonstrando monitoramento
				contínuo e atuação corretiva.
			</>
		),
		plumaa: (
			<>
				<strong>Painel online com evolução mensal</strong>, exportável
				em PDF a qualquer momento.
			</>
		),
	},
	{
		lei: (
			<>
				<strong>Reaplicação periódica</strong> em janela máxima de 12
				meses, conforme a norma.
			</>
		),
		plumaa: (
			<>
				Aplicação <strong>automática mensal</strong> — 12 ciclos em um
				ano, sem precisar lembrar.
			</>
		),
	},
	{
		lei: (
			<>
				<strong>Anonimato individual</strong> e conformidade com LGPD no
				tratamento dos dados.
			</>
		),
		plumaa: (
			<>
				Anonimato técnico real: empresa{' '}
				<strong>só vê dado agregado</strong>. LGPD em dia.
			</>
		),
	},
];

const DOMINIOS: ReadonlyArray<{ num: string; title: string; desc: string }> = [
	{
		num: '01',
		title: 'Cansaço e tensão',
		desc: 'Frequência de exaustão e estresse no último mês.',
	},
	{
		num: '02',
		title: 'Exigências no trabalho',
		desc: 'Volume, ritmo e carga emocional do dia a dia.',
	},
	{
		num: '03',
		title: 'Trabalho vs vida pessoal',
		desc: 'Quanto o trabalho consome a energia de fora dele.',
	},
	{
		num: '04',
		title: 'Saúde geral',
		desc: 'Como a pessoa avalia a própria saúde no geral.',
	},
	{
		num: '05',
		title: 'Satisfação',
		desc: 'Avaliação geral do trabalho pela equipe.',
	},
	{
		num: '06',
		title: 'Comportamentos ofensivos',
		desc: 'Assédio, violência, atenção indesejada.',
	},
	{
		num: '07',
		title: 'Influência e autonomia',
		desc: 'Quanto a pessoa decide o próprio trabalho.',
	},
	{
		num: '08',
		title: 'Liderança',
		desc: 'Clareza, apoio e organização da chefia direta.',
	},
	{
		num: '09',
		title: 'Relações interpessoais',
		desc: 'Qualidade dos vínculos com colegas no dia a dia.',
	},
	{
		num: '10',
		title: 'Valores no trabalho',
		desc: 'Justiça, confiança e respeito mútuo.',
	},
	{
		num: '11',
		title: 'Significado e propósito',
		desc: 'Sentido do que se faz e vínculo com a empresa.',
	},
];

const dominioCardCls =
	'flex min-w-0 flex-col rounded-[14px] border border-black/[0.06] bg-[#FFFFFD] p-6 transition hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(26,26,26,0.08)] md:p-7';

/** Mesma largura de uma célula do grid 4 colunas (gap-3.5). */
const dominioCardWidthCls =
	'w-full sm:w-[calc((100%-0.875rem)/2)] lg:w-[calc((100%-2.625rem)/4)]';

function DominioCard({
	d,
	className,
}: {
	d: (typeof DOMINIOS)[number];
	className?: string;
}) {
	return (
		<div className={cn(dominioCardCls, className)}>
			<div className="!font-libre mb-[18px] text-4xl font-medium italic leading-none tracking-[-0.02em] text-[#E06A30]">
				{d.num}
			</div>
			<h4 className="!font-libre mb-2 text-[19px] font-semibold leading-snug text-[#1A1A1A]">
				{d.title}
			</h4>
			<p className="text-[13px] leading-relaxed text-[#3A3A3A]">
				{d.desc}
			</p>
		</div>
	);
}

const PORTES: ReadonlyArray<{ label: string; value: number }> = [
	{ label: 'Até 10', value: 10 },
	{ label: '11 a 30', value: 25 },
	{ label: '31 a 100', value: 50 },
	{ label: '100 a 500', value: 200 },
	{ label: '500 +', value: 1000 },
];

const HEAT_CELLS: ReadonlyArray<[string, string, string]> = [
	['#FBF1DA', '#8B6E1F', '3.0'],
	['#FBF1DA', '#8B6E1F', '2.7'],
	['#FBF1DA', '#8B6E1F', '2.7'],
	['#FBF1DA', '#8B6E1F', '2.6'],
	['#E8F2EC', '#2D8B5F', '4.0'],
	['#E8F2EC', '#2D8B5F', '4.4'],
	['#E8F2EC', '#2D8B5F', '4.5'],
	['#E8F2EC', '#2D8B5F', '4.7'],
	['#E8F2EC', '#2D8B5F', '4.7'],
	['#E8F2EC', '#2D8B5F', '1.6'],
	['#E8F2EC', '#2D8B5F', '1.0'],
];

function formatBR(n: number) {
	return `R$ ${Math.round(n).toLocaleString('pt-BR')}`;
}

function formatBRdec(n: number) {
	return `R$ ${n.toFixed(2).replace('.', ',')}`;
}

function getValorColab(colab: number) {
	if (colab <= 10) return 15;
	if (colab <= 30) return 12;
	if (colab <= 100) return 8;
	return 5;
}

const eyebrowCls =
	'mb-[18px] inline-block text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]';

const sectionTitleCls =
	'!font-libre mb-7 text-[clamp(2rem,5vw,3.625rem)] font-semibold leading-[1.1] tracking-[-0.06em] text-[#1A1A1A] [&_em]:!font-libre [&_em]:font-medium [&_em]:italic [&_em]:text-[#E06A30]';

const subCls =
	'mx-auto mb-10 max-w-[620px] text-lg font-light leading-relaxed text-[#3A3A3A]';

const ctaPrimaryCls =
	'inline-flex items-center gap-2.5 rounded-full bg-gradient-to-br from-[#E06A30] to-[#FA8046] px-9 py-5 text-[15px] font-semibold text-white no-underline transition hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(224,106,48,0.3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[#E06A30] focus-visible:rounded-full';

function ApresentacaoCalculator() {
	const [colab, setColab] = useState(50);

	const derived = useMemo(() => {
		let c = Number.isFinite(colab) ? Math.floor(colab) : 50;
		if (c < 1) c = 1;
		if (c > 9999) c = 9999;
		const piso = c * 1800;
		const teto = c * 6700;
		const valorPorColab = getValorColab(c);
		const mensal = c * valorPorColab;
		const anual = mensal * 12;
		const porColabDia = valorPorColab / 30;
		const pct = (anual / piso) * 100;
		const anos = piso / anual;
		return { c, piso, teto, mensal, anual, porColabDia, pct, anos };
	}, [colab]);

	const { c, piso, teto, mensal, anual, porColabDia, pct, anos } = derived;

	const onMinus = () => {
		setColab((prev) => {
			const val = (Number.isFinite(prev) ? Math.floor(prev) : 1) || 1;
			const step = val <= 30 ? 1 : val <= 100 ? 5 : val <= 500 ? 10 : 50;
			return Math.max(1, val - step);
		});
	};

	const onPlus = () => {
		setColab((prev) => {
			const val = (Number.isFinite(prev) ? Math.floor(prev) : 1) || 1;
			const step = val < 30 ? 1 : val < 100 ? 5 : val < 500 ? 10 : 50;
			return Math.min(9999, val + step);
		});
	};

	return (
		<section
			id="calculadora"
			className="w-full scroll-mt-24 bg-[#FBF1DA] px-6 py-[70px] md:px-[60px] md:py-[110px]"
		>
			<div className="mx-auto w-full max-w-[1180px]">
				<div className="mb-14 text-center">
					<div className={eyebrowCls}>
						Sua exposição vs seu investimento
					</div>
					<h2 className={`${sectionTitleCls} mx-auto max-w-[780px]`}>
						Faça a conta <em>da sua empresa</em>.
					</h2>
					<p className={`${subCls} mt-4`}>
						Use os atalhos rápidos abaixo ou digite o número exato
						de colaboradores. A multa da NR-1 cresce por
						colaborador; a Plumaa fica mais barata por colaborador.
					</p>
				</div>

				<div className="rounded-3xl border border-black/[0.06] bg-[#FFFFFD] px-6 py-9 shadow-[0_8px_40px_rgba(26,26,26,0.05)] md:px-12 md:py-14">
					<div className="mb-9 flex flex-wrap justify-center gap-2.5">
						{PORTES.map(({ label, value }) => (
							<button
								key={value}
								type="button"
								className={cn(
									'rounded-full border border-black/[0.12] bg-white px-4 py-2 text-xs font-medium text-[#3A3A3A] transition hover:border-[#E06A30] hover:bg-[rgba(224,106,48,0.06)] hover:text-[#E06A30]',
									c === value &&
										'border-[#E06A30] bg-[#E06A30] font-semibold text-white hover:bg-[#E06A30] hover:text-white',
								)}
								onClick={() => setColab(value)}
							>
								{label}
							</button>
						))}
					</div>

					<div className="mb-12 flex flex-col flex-wrap items-center justify-center gap-6 text-center md:flex-row md:gap-6">
						<span className="!font-libre text-2xl leading-snug text-[#3A3A3A] md:text-[1.65rem]">
							A sua empresa tem
						</span>
						<div className="inline-flex min-w-[13.5rem] shrink-0 items-center rounded-full border border-black/[0.08] bg-[#FBF1DA] p-1.5 sm:min-w-[14.5rem]">
							<button
								type="button"
								className="flex size-10 shrink-0 items-center justify-center rounded-full border border-black/[0.1] bg-white text-xl font-normal text-[#1A1A1A] transition hover:border-[#E06A30] hover:bg-[#E06A30] hover:text-white"
								onClick={onMinus}
								aria-label="Diminuir"
							>
								−
							</button>
							<input
								type="number"
								value={c}
								min={1}
								max={9999}
								className="!font-libre min-w-[6.5ch] flex-1 border-0 bg-transparent px-2 text-center text-[2rem] font-semibold tabular-nums text-[#1A1A1A] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
								onChange={(e) => {
									const v = parseInt(e.target.value, 10);
									setColab(Number.isFinite(v) ? v : 1);
								}}
							/>
							<button
								type="button"
								className="flex size-10 shrink-0 items-center justify-center rounded-full border border-black/[0.1] bg-white text-xl font-normal text-[#1A1A1A] transition hover:border-[#E06A30] hover:bg-[#E06A30] hover:text-white"
								onClick={onPlus}
								aria-label="Aumentar"
							>
								+
							</button>
						</div>
						<span className="!font-libre text-2xl leading-snug text-[#3A3A3A] md:text-[1.65rem]">
							colaboradores
						</span>
					</div>

					<div className="overflow-hidden rounded-2xl border border-black/[0.08] md:grid md:[grid-template-columns:minmax(0,1fr)_minmax(0,1fr)]">
						<div className="w-full min-w-0 border-b border-black/[0.08] bg-gradient-to-br from-[rgba(200,54,47,0.04)] to-[rgba(224,106,48,0.04)] px-6 py-9 text-center md:border-b-0 md:border-r md:border-black/[0.08]">
							<div className="mb-3.5 text-[11px] font-semibold uppercase tracking-[2px] text-[#C8362F]">
								Sua exposição à multa
							</div>
							<div className="flex w-full max-w-full items-center justify-center gap-4">
								<div className="flex-1 text-center">
									<div className="!font-libre text-[clamp(1.75rem,4vw,2.75rem)] font-semibold leading-none tracking-[-0.04em] text-[#C8362F]">
										{formatBR(piso)}
									</div>
									<div className="mt-2 text-[10px] font-semibold uppercase tracking-[1.5px] text-[#767676]">
										no piso
									</div>
								</div>
								<div className="h-[50px] w-px bg-[rgba(200,54,47,0.2)]" />
								<div className="flex-1 text-center">
									<div className="!font-libre text-[clamp(1.75rem,4vw,2.75rem)] font-semibold leading-none tracking-[-0.04em] text-[#C8362F]">
										{formatBR(teto)}
									</div>
									<div className="mt-2 text-[10px] font-semibold uppercase tracking-[1.5px] text-[#767676]">
										no teto
									</div>
								</div>
							</div>
							<div className="mt-2 text-xs text-[#767676]">
								R$ 1.800 a R$ 6.700 por colaborador, com
								reincidência
							</div>
						</div>

						<div className="w-full min-w-0 bg-[#FBF1DA] px-6 py-9 text-center">
							<div className="mb-3.5 text-[11px] font-semibold uppercase tracking-[2px] text-[#E06A30]">
								Seu investimento Plumaa
							</div>
							<div className="!font-libre text-[clamp(3.5rem,8vw,5.5rem)] font-semibold leading-none tracking-[-0.08em] text-[#E06A30]">
								{formatBRdec(porColabDia)}
							</div>
							<div className="mb-4 text-[13px] font-semibold uppercase tracking-[1.5px] text-[#3A3A3A]">
								por colaborador, por dia
							</div>
							<div className="!font-libre border-t border-black/[0.08] pt-3.5 text-[15px] italic text-[#3A3A3A]">
								<span className="!font-libre font-semibold not-italic text-[#1A1A1A]">
									{formatBR(mensal)}
								</span>{' '}
								por mês ·{' '}
								<span className="!font-libre font-semibold not-italic text-[#1A1A1A]">
									{formatBR(anual)}
								</span>{' '}
								ao ano
							</div>
						</div>
					</div>

					<div className="mt-9 rounded-br-xl rounded-tr-xl border-l-[3px] border-[#E06A30] bg-[rgba(224,106,48,0.06)] px-6 py-6 md:px-7">
						<p className="!font-libre [&_strong]:!font-libre text-lg italic leading-relaxed text-[#1A1A1A] [&_strong]:font-semibold [&_strong]:not-italic [&_strong]:text-[#E06A30]">
							A Plumaa anual custa{' '}
							<strong>{pct.toFixed(1).replace('.', ',')}%</strong>{' '}
							da multa mínima. Ou seja, mesmo se você fosse
							multado <strong>uma única vez</strong>, a Plumaa
							estaria paga por{' '}
							<strong>{Math.round(anos)} anos</strong>.
						</p>
					</div>
				</div>
			</div>
		</section>
	);
}

const ApresentacaoContent = () => {
	return (
		<>
			<section className="flex min-h-[92vh] items-center px-6 pb-28 pt-16 md:px-[60px] md:pb-[110px] md:pt-[60px]">
				<div className="mx-auto w-full max-w-[780px] px-6 md:px-[60px]">
					<div className={eyebrowCls}>
						NR-1 · A nova lei de saúde mental no trabalho
					</div>
					<h1 className="!font-libre [&_em]:!font-libre mb-7 text-[clamp(2.25rem,5.5vw,4rem)] font-semibold leading-[1.08] tracking-[-0.06em] text-[#1A1A1A] [&_em]:font-medium [&_em]:italic [&_em]:text-[#E06A30]">
						Sua NR-1,
						<br />
						<em>sem trabalho extra para o seu RH.</em>
					</h1>
					<p className={subCls}>
						Tecnologia, inteligência artificial e expertise médica
						em uma plataforma só. A Plumaa cumpre a NR-1 da sua
						empresa do início ao fim — você só abre o painel.
					</p>
					<div className="mb-10 flex flex-wrap gap-3">
						{[
							'Sem implementação técnica',
							'Primeiro mês grátis',
							'Laudo com CRM ativo',
						].map((t) => (
							<div
								key={t}
								className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-[#FFFFFD] px-[18px] py-2.5 text-[13px] font-medium text-[#3A3A3A]"
							>
								<span className="size-1.5 shrink-0 rounded-full bg-[#E06A30]" />
								{t}
							</div>
						))}
					</div>
					<a href="#calculadora" className={ctaPrimaryCls}>
						Ver minha exposição →
					</a>
				</div>
			</section>

			<section className="w-full px-6 py-[70px] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[1180px]">
					<div className="mb-14 text-center">
						<div className={eyebrowCls}>Por que a Plumaa</div>
						<h2
							className={`${sectionTitleCls} mx-auto max-w-[780px]`}
						>
							Três coisas <em>juntas</em> em um único lugar.
						</h2>
					</div>

					<div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-6">
						{[
							{
								title: (
									<>
										Expertise <em>médica</em>
									</>
								),
								body: 'Cada laudo é assinado por médica com CRM ativo, responsabilidade técnica integral. Em fiscalização e em ação judicial, médico é o que pesa — não psicólogo, não consultoria, não RH.',
								svg: (
									<path d="M20 4 L34 11 V20 C34 28 28 34 20 36 C12 34 6 28 6 20 V11 Z M20 14 V24 M15 19 H25" />
								),
							},
							{
								title: (
									<>
										Inteligência <em>artificial</em>
									</>
								),
								body: 'Análise contínua dos dados coletados todo mês. A IA identifica padrões antes que apareçam em afastamento — e direciona a palestra de intervenção para o ponto crítico real do seu time.',
								svg: (
									<>
										<circle cx={20} cy={20} r={6} />
										<path d="M20 8 V12 M20 28 V32 M8 20 H12 M28 20 H32" />
										<path d="M11.5 11.5 L14 14 M26 26 L28.5 28.5 M11.5 28.5 L14 26 M26 14 L28.5 11.5" />
									</>
								),
							},
							{
								title: (
									<>
										Validação <em>internacional</em>
									</>
								),
								body: 'Questionários usados em mais de 40 países, validados academicamente, aceitos pelo Ministério do Trabalho. Aplicamos a versão padrão de 11 áreas mensalmente; a versão expandida de 29 áreas está disponível para análises mais profundas em parcerias contínuas.',
								svg: (
									<>
										<rect
											x={6}
											y={14}
											width={6}
											height={20}
											rx={1}
										/>
										<rect
											x={17}
											y={8}
											width={6}
											height={26}
											rx={1}
										/>
										<rect
											x={28}
											y={20}
											width={6}
											height={14}
											rx={1}
										/>
										<path d="M4 6 L36 6" />
									</>
								),
							},
						].map((card) => (
							<div
								key={String(card.title)}
								className="relative overflow-hidden rounded-2xl border border-black/[0.06] bg-[#FFFFFD] p-8 shadow-[0_2px_16px_rgba(26,26,26,0.03)] before:pointer-events-none before:absolute before:left-0 before:right-0 before:top-0 before:h-[3px] before:bg-gradient-to-r before:from-[#E06A30] before:to-[#FA8046]"
							>
								<svg
									className="mb-6 text-[#E06A30]"
									width={40}
									height={40}
									viewBox="0 0 40 40"
									fill="none"
									stroke="currentColor"
									strokeWidth={1.8}
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									{card.svg}
								</svg>
								<h3 className="!font-libre [&_em]:!font-libre mb-3 text-2xl font-semibold leading-snug text-[#1A1A1A] [&_em]:italic [&_em]:text-[#E06A30]">
									{card.title}
								</h3>
								<p className="text-sm leading-relaxed text-[#3A3A3A]">
									{card.body}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="w-full bg-[#FBF1DA] px-6 py-[70px] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[1180px]">
					<div className="mb-14 text-center">
						<div className={eyebrowCls}>A nova exigência</div>
						<h2
							className={`${sectionTitleCls} mx-auto max-w-[780px]`}
						>
							O que a lei pede e <em>o que entregamos</em>.
						</h2>
						<p className={`${subCls} mt-4`}>
							A partir de 26 de maio de 2026, toda empresa CLT
							precisa cumprir esta lista. Cada linha é uma
							exigência da NR-1 — e o que entra como entrega da
							Plumaa.
						</p>
					</div>

					<div className="mx-auto mb-8 flex w-fit max-w-full items-center gap-4 rounded-full bg-gradient-to-br from-[#E06A30] to-[#FA8046] px-7 py-3.5 text-white shadow-[0_8px_24px_rgba(224,106,48,0.25)]">
						<div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-[#E06A30]">
							✓
						</div>
						<div className="text-[13px] font-semibold uppercase tracking-[1.5px]">
							Em conformidade com 100% da NR-1
						</div>
					</div>

					<div className="relative overflow-hidden rounded-[20px] border border-[rgba(224,106,48,0.15)] bg-[#FFFFFD] px-5 py-7 shadow-[0_8px_40px_rgba(26,26,26,0.05)] before:absolute before:left-0 before:right-0 before:top-0 before:h-1 before:bg-gradient-to-r before:from-[#E06A30] before:to-[#FA8046] md:px-14 md:py-12">
						<div className="hidden grid-cols-[1fr_48px_1fr] gap-3 border-b-[1.5px] border-black/10 pb-6 md:grid">
							<div className="text-[11px] font-semibold uppercase tracking-[2.5px] text-[#767676]">
								O que a lei exige
							</div>
							<div />
							<div className="text-right text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]">
								Como a Plumaa entrega
							</div>
						</div>

						{CERT_ROWS.map((row, idx) => (
							<div
								key={idx}
								className="grid gap-2 border-b border-dashed border-black/[0.07] py-4 last:border-b-0 md:grid-cols-[1fr_48px_1fr] md:items-center md:gap-3 md:py-5"
							>
								<div className="text-[15px] leading-relaxed text-[#3A3A3A] before:text-[#767676] max-md:before:content-['⊙_'] md:before:content-none [&_strong]:font-semibold [&_strong]:text-[#1A1A1A]">
									{row.lei}
								</div>
								<div className="relative hidden items-center justify-center md:flex">
									<div className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-[#E06A30] to-transparent" />
									<div className="relative z-[2] flex size-7 items-center justify-center rounded-full bg-[#E06A30] text-sm font-bold text-white shadow-[0_2px_8px_rgba(224,106,48,0.25)]">
										✓
									</div>
								</div>
								<div className="text-[15px] leading-relaxed text-[#1A1A1A] max-md:before:font-bold max-md:before:text-[#E06A30] max-md:before:content-['✓_'] md:text-right [&_strong]:font-semibold [&_strong]:text-[#E06A30]">
									{row.plumaa}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="w-full px-6 py-[70px] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[1180px]">
					<div className="mb-14 text-center">
						<div className={eyebrowCls}>O que medimos</div>
						<h2
							className={`${sectionTitleCls} mx-auto max-w-[780px]`}
						>
							Onze áreas, <em>todo mês</em>.
						</h2>
						<p className={`${subCls} mt-4`}>
							Cada uma é uma frente de saúde mental no trabalho. A
							Plumaa mede todas e classifica por cor: verde
							(saudável), amarelo (atenção), vermelho (ação
							prioritária).
						</p>
					</div>

					<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
						{DOMINIOS.slice(0, 8).map((d) => (
							<DominioCard key={d.num} d={d} />
						))}
					</div>
					<div className="mt-3.5 flex flex-wrap justify-center gap-3.5">
						{DOMINIOS.slice(8).map((d) => (
							<DominioCard
								key={d.num}
								d={d}
								className={dominioCardWidthCls}
							/>
						))}
					</div>
				</div>
			</section>

			<ApresentacaoCalculator />

			<section className="w-full px-6 py-[70px] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[1180px]">
					<div className="mb-14 text-center">
						<div className={eyebrowCls}>O que você recebe</div>
						<h2
							className={`${sectionTitleCls} mx-auto max-w-[780px]`}
						>
							Três entregas
						</h2>
					</div>

					<div className="flex flex-col gap-6">
						<div className="grid gap-8 rounded-[20px] border border-black/[0.06] bg-[#FFFFFD] p-8 shadow-[0_4px_24px_rgba(26,26,26,0.04)] md:grid-cols-[minmax(280px,480px)_1fr] md:items-center md:gap-14 md:p-12">
							<Link
								to="/demonstracao_dashboard"
								className="group relative flex min-h-[280px] cursor-pointer items-center justify-center overflow-hidden rounded-[14px] bg-[#FBF1DA] p-8 no-underline outline-none ring-offset-2 transition-shadow focus-visible:ring-2 focus-visible:ring-[#E06A30]"
								aria-label="Ver demonstração do painel: tudo do seu time numa tela só"
							>
								<div className="relative z-0 flex w-full flex-col gap-3 rounded-xl bg-white p-[18px] shadow-[0_2px_16px_rgba(26,26,26,0.04)]">
									<div className="flex items-center justify-between border-b border-black/[0.05] pb-2.5">
										<div className="!font-libre text-sm font-semibold">
											Painel · Maio 2026
										</div>
										<div className="text-[9px] font-semibold uppercase tracking-[1.5px] text-[#E06A30]">
											ATUALIZADO HOJE
										</div>
									</div>
									<div className="grid grid-cols-3 gap-2">
										{[
											['92%', '#E06A30', 'Participação'],
											['41', '#2D8B5F', 'Esgotamento'],
											['4', '#E0A52E', 'Atenção'],
										].map(([n, col, lbl]) => (
											<div
												key={lbl}
												className="rounded-lg bg-[#F7F4EE] px-2.5 py-3 text-center"
											>
												<div
													className="!font-libre text-2xl font-semibold leading-none tracking-[-0.04em]"
													style={{
														color: col as string,
													}}
												>
													{n}
												</div>
												<div className="mt-1.5 text-[8px] font-semibold uppercase tracking-[1px] text-[#767676]">
													{lbl}
												</div>
											</div>
										))}
									</div>
									<div className="grid grid-cols-11 gap-1 pt-1">
										{HEAT_CELLS.map(([bg, col, val], i) => (
											<div
												key={`${val}-${i}`}
												className="!font-libre flex aspect-square items-center justify-center rounded text-[9px] font-semibold"
												style={{
													backgroundColor: bg,
													color: col,
												}}
											>
												{val}
											</div>
										))}
									</div>
								</div>
								<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[14px] bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100">
									<span className="font-albert inline-flex items-center gap-2 rounded-full border border-white/50 px-5 py-2 text-base font-semibold tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] md:px-6 md:py-2.5 md:text-lg">
										Ver mais
										<ChevronRight
											className="size-5 shrink-0 md:size-6"
											strokeWidth={2.25}
											aria-hidden
										/>
									</span>
								</div>
							</Link>
							<div>
								<div className="!font-libre mb-4 text-[44px] font-medium italic leading-none tracking-[-0.02em] text-[#E06A30]">
									01
								</div>
								<div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]">
									Painel online
								</div>
								<h3 className="!font-libre [&_em]:!font-libre mb-4 text-[clamp(1.625rem,3vw,2rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-[#1A1A1A] [&_em]:italic [&_em]:text-[#E06A30]">
									Tudo do seu time, <em>numa tela só</em>.
								</h3>
								<p className="text-base leading-relaxed text-[#3A3A3A] [&_strong]:font-semibold [&_strong]:text-[#1A1A1A]">
									Abre no celular ou no computador, atualiza
									automaticamente todo mês conforme as
									respostas chegam.{' '}
									<strong>
										Participação, índice de esgotamento,
										áreas em alerta, mapa de calor por setor
									</strong>{' '}
									— tudo em tempo real. Filtra por
									departamento e exporta em PDF a qualquer
									momento.
								</p>
							</div>
						</div>

						<div className="grid gap-8 rounded-[20px] border border-black/[0.06] bg-[#FFFFFD] p-8 shadow-[0_4px_24px_rgba(26,26,26,0.04)] md:grid-cols-[1fr_minmax(280px,480px)] md:items-center md:gap-14 md:p-12">
							<div className="order-2 md:order-1">
								<div className="!font-libre mb-4 text-[44px] font-medium italic leading-none tracking-[-0.02em] text-[#E06A30]">
									02
								</div>
								<div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]">
									Laudo técnico
								</div>
								<h3 className="!font-libre [&_em]:!font-libre mb-4 text-[clamp(1.625rem,3vw,2rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-[#1A1A1A] [&_em]:italic [&_em]:text-[#E06A30]">
									Laudo <em>assinado por médica com CRM</em>.
								</h3>
								<p className="text-base leading-relaxed text-[#3A3A3A] [&_strong]:font-semibold [&_strong]:text-[#1A1A1A]">
									Documento técnico completo, com tudo que a
									lei pede e o fiscal pode perguntar —
									contexto regulatório, metodologia,
									resultados, plano de ação e{' '}
									<strong>assinatura médica oficial</strong>.
									Pronto pra anexar ao programa oficial de
									gestão de riscos da empresa (PGR), bem
									documentado, sem você precisar mexer.
								</p>
							</div>
							<Link
								to="/demonstracao_laudo#resultados-7-2"
								className="group relative order-1 flex min-h-[280px] cursor-pointer items-center justify-center overflow-hidden rounded-[14px] bg-[#FBF1DA] p-8 no-underline outline-none ring-offset-2 transition-shadow focus-visible:ring-2 focus-visible:ring-[#E06A30] md:order-2"
								aria-label="Ver demonstração do laudo técnico assinado por médica com CRM"
							>
								<div className="relative z-0 w-full max-w-[280px] -rotate-2 rounded-lg border border-black/[0.08] bg-white px-8 py-7 shadow-[0_8px_28px_rgba(26,26,26,0.1)] after:absolute after:left-0 after:right-0 after:top-0 after:h-[3px] after:rounded-t-lg after:bg-gradient-to-r after:from-[#E06A30] after:to-[#FA8046]">
									<div className="mb-3.5 text-[8px] font-semibold uppercase tracking-[1.5px] text-[#E06A30]">
										PLUMAA · LAUDO TÉCNICO
									</div>
									<div className="!font-libre mb-3.5 text-base font-semibold leading-snug text-[#1A1A1A]">
										Avaliação de Riscos Psicossociais
										Relacionados ao Trabalho
									</div>
									<div className="mb-4 flex flex-col gap-1.5">
										<div className="h-[3px] w-full rounded-full bg-black/[0.08]" />
										<div className="h-[3px] w-[88%] rounded-full bg-black/[0.08]" />
										<div className="h-[3px] w-full rounded-full bg-black/[0.08]" />
										<div className="h-[3px] w-[70%] rounded-full bg-black/[0.08]" />
										<div className="h-[3px] w-[88%] rounded-full bg-black/[0.08]" />
									</div>
									<div className="border-t border-black/[0.08] pt-3.5">
										<div className="mb-1 text-[8px] uppercase tracking-[1px] text-[#767676]">
											RESPONSÁVEL TÉCNICA
										</div>
										<div className="!font-libre text-[13px] font-medium italic text-[#1A1A1A]">
											Dra. Ana Gabriela A. da Silva
										</div>
										<div className="mt-0.5 text-[9px] text-[#767676]">
											CRM-TO 7259 · Em conformidade com a
											NR-1
										</div>
									</div>
								</div>
								<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[14px] bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100">
									<span className="font-albert inline-flex items-center gap-2 rounded-full border border-white/50 px-5 py-2 text-base font-semibold tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] md:px-6 md:py-2.5 md:text-lg">
										Ver mais
										<ChevronRight
											className="size-5 shrink-0 md:size-6"
											strokeWidth={2.25}
											aria-hidden
										/>
									</span>
								</div>
							</Link>
						</div>

						<div className="grid gap-8 rounded-[20px] border border-black/[0.06] bg-[#FFFFFD] p-8 shadow-[0_4px_24px_rgba(26,26,26,0.04)] md:grid-cols-[minmax(280px,480px)_1fr] md:items-center md:gap-14 md:p-12">
							<Link
								to="/demonstracao_devolutiva#atencao-1"
								className="group relative flex min-h-[280px] cursor-pointer items-center justify-center overflow-hidden rounded-[14px] bg-[#FBF1DA] p-8 no-underline outline-none ring-offset-2 transition-shadow focus-visible:ring-2 focus-visible:ring-[#E06A30]"
								aria-label="Ver demonstração da devolutiva editorial a partir de Exigências no trabalho"
							>
								<div className="relative z-0 w-full rounded-xl bg-white px-6 py-6 shadow-[0_4px_20px_rgba(26,26,26,0.05)]">
									<div className="text-[9px] font-semibold uppercase tracking-[1.5px] text-[#E06A30]">
										DEVOLUTIVA EDITORIAL · MAIO 2026
									</div>
									<div className="!font-libre [&_em]:!font-libre my-2.5 text-lg font-semibold leading-snug text-[#1A1A1A] [&_em]:italic [&_em]:text-[#E06A30]">
										Conflito trabalho-família ·{' '}
										<em>zona amarela</em>
									</div>
									<div className="!font-libre mb-3.5 rounded-br-lg border-l-[3px] border-[#E06A30] bg-[rgba(224,106,48,0.06)] px-[18px] py-3.5 text-base italic leading-relaxed text-[#3A3A3A]">
										&ldquo;Quanto o trabalho consome a
										energia que sobra para a vida
										pessoal.&rdquo;
									</div>
									<div className="text-xs leading-relaxed text-[#767676]">
										Em vez de{' '}
										<em>
											&ldquo;subescala 7 do COPSOQ
											II-Br&rdquo;
										</em>
										, linguagem de gestão.
										<br />
										<strong className="!font-libre font-semibold text-[#E06A30]">
											7 verdes · 4 amarelos · 0 vermelhos
										</strong>
									</div>
								</div>
								<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[14px] bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/40 group-hover:opacity-100">
									<span className="font-albert inline-flex items-center gap-2 rounded-full border border-white/50 px-5 py-2 text-base font-semibold tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)] md:px-6 md:py-2.5 md:text-lg">
										Ver mais
										<ChevronRight
											className="size-5 shrink-0 md:size-6"
											strokeWidth={2.25}
											aria-hidden
										/>
									</span>
								</div>
							</Link>
							<div>
								<div className="!font-libre mb-4 text-[44px] font-medium italic leading-none tracking-[-0.02em] text-[#E06A30]">
									03
								</div>
								<div className="mb-2.5 text-[11px] font-semibold uppercase tracking-[2.5px] text-[#E06A30]">
									Devolutiva editorial · só nossa
								</div>
								<h3 className="!font-libre [&_em]:!font-libre mb-4 text-[clamp(1.625rem,3vw,2rem)] font-semibold leading-[1.15] tracking-[-0.02em] text-[#1A1A1A] [&_em]:italic [&_em]:text-[#E06A30]">
									A tradução que <em>a diretoria entende</em>.
								</h3>
								<p className="text-base leading-relaxed text-[#3A3A3A] [&_strong]:font-semibold [&_strong]:text-[#1A1A1A]">
									Esse é o nosso diferencial. O mercado
									entrega só o laudo técnico cru — ninguém na
									diretoria abre. A Plumaa entrega{' '}
									<strong>
										também a devolutiva editorial
									</strong>
									, em linguagem de gestão, com &quot;7
									verdes, 4 amarelos, 0 vermelhos&quot; no
									lugar de jargão. Você leva pra reunião como
									pauta, não como auditoria.
								</p>
							</div>
						</div>
					</div>

					<div className="mt-9 grid gap-6 rounded-[20px] bg-gradient-to-br from-[#E06A30] to-[#FA8046] p-8 text-white shadow-[0_12px_36px_rgba(224,106,48,0.2)] md:grid-cols-[auto_1fr] md:items-center md:gap-6 md:p-8">
						<div className="mx-auto flex size-16 shrink-0 items-center justify-center rounded-full bg-white md:mx-0">
							<svg
								width={36}
								height={36}
								viewBox="0 0 36 36"
								fill="none"
								stroke="#E06A30"
								strokeWidth={2}
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<path d="M18 4 L31 9 V18 C31 25 26 30 18 32 C10 30 5 25 5 18 V9 Z" />
								<path d="M18 13 V23 M13 18 H23" />
							</svg>
						</div>
						<div className="text-center md:text-left">
							<div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[2px] text-white/80">
								Diferencial único no mercado
							</div>
							<div className="!font-libre [&_em]:!font-libre text-[22px] font-semibold leading-snug [&_em]:italic">
								Somos a <em>única plataforma</em> com laudo
								psicossocial assinado por médica com CRM ativo.
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className="w-full bg-[#FBF1DA] px-6 py-[70px] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[780px] px-6 md:px-[60px]">
					<div className="mb-12 text-center">
						<div className={eyebrowCls}>Quatro motivos</div>
						<h2 className={`${sectionTitleCls} mx-auto`}>
							Por que <em>com a Plumaa</em> vale mais.
						</h2>
					</div>

					<div className="rounded-[20px] border border-black/[0.06] bg-[#FFFFFD] px-6 py-4 md:px-9">
						{[
							{
								mark: '01',
								h: (
									<>
										Laudo com{' '}
										<em>responsabilidade médica</em>
									</>
								),
								p: 'Outras plataformas entregam laudo feito por psicólogo, RH ou consultoria. A Plumaa é a única com laudo assinado por médica com CRM ativo. Em fiscalização e em ação trabalhista, isso é o que separa "documento" de "prova técnica".',
							},
							{
								mark: '02',
								h: (
									<>
										Devolutiva{' '}
										<em>traduzida pra diretoria</em>
									</>
								),
								p: 'O mercado entrega só o laudo cru — ninguém na diretoria abre. A Plumaa entrega também a devolutiva editorial em linguagem de gestão, com "7 verdes, 4 amarelos, 0 vermelhos" em vez de jargão.',
							},
							{
								mark: '03',
								h: (
									<>
										Uma palestra/mês{' '}
										<em>escolhida pelos dados</em>
									</>
								),
								p: 'Concorrente entrega dado e te deixa sozinho. A Plumaa entrega dado + intervenção: um encontro mensal com tema escolhido pelos pontos críticos do mês. Plano de ação que o fiscal pede já feito.',
							},
							{
								mark: '04',
								h: (
									<>
										Implementação <em>sem fricção</em>
									</>
								),
								p: 'Concorrente cobra consultoria, integração e kickoff antes de qualquer entrega. A Plumaa entra direto: você manda a lista da equipe, o questionário chega no celular de cada colaborador. Sem TI, sem treinamento, sem reunião de implantação.',
							},
						].map((item) => (
							<div
								key={item.mark}
								className="grid grid-cols-[40px_1fr] gap-4 border-b border-black/[0.08] py-5 last:border-b-0 md:grid-cols-[56px_1fr] md:gap-6 md:py-7"
							>
								<div className="!font-libre pt-0.5 text-[22px] font-medium italic leading-none text-[#E06A30] md:text-[28px]">
									{item.mark}
								</div>
								<div>
									<h4 className="!font-libre [&_em]:!font-libre mb-2 text-[19px] font-semibold leading-snug text-[#1A1A1A] md:text-[22px] [&_em]:italic [&_em]:text-[#E06A30]">
										{item.h}
									</h4>
									<p className="text-[15px] leading-relaxed text-[#3A3A3A]">
										{item.p}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="w-full bg-[#1A1A1A] px-6 py-[70px] text-center text-[#FFFFFD] md:px-[60px] md:py-[110px]">
				<div className="mx-auto w-full max-w-[780px] px-6 md:px-[60px]">
					<img
						src={LOGO_CTA_LIGHT}
						alt="Plumaa"
						className="mx-auto mb-9 h-auto w-[72px] opacity-95"
					/>
					<div className={`${eyebrowCls} mb-0 text-[#FA8046]`}>
						Próximo passo
					</div>
					<h2 className="!font-libre [&_em]:!font-libre my-6 text-[clamp(2.5rem,7vw,4.5rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-white [&_em]:italic [&_em]:text-[#FA8046]">
						O <em>primeiro mês</em>
						<br />é grátis.
					</h2>
					<p className="mx-auto mb-12 max-w-[580px] text-[17px] leading-relaxed text-white/70">
						Sem cartão de crédito, sem compromisso. Você roda o
						questionário com a equipe, recebe o primeiro painel,
						laudo e devolutiva. Decide se segue depois.
					</p>
					<div className="mb-12 flex flex-wrap justify-center gap-4">
						<Link to="/cadastro" className={ctaPrimaryCls}>
							Começar gratuitamente →
						</Link>
						<a
							href={WHATSAPP_LINK_NR1}
							className={`${ctaPrimaryCls} border border-white/20 bg-transparent hover:bg-white/10 hover:shadow-none`}
						>
							Falar no WhatsApp
						</a>
					</div>
					<div className="flex flex-wrap justify-center gap-4 text-[13px] text-white/50">
						<span>Sem implementação técnica</span>
						<span>·</span>
						<span>1º mês sem cobrança</span>
						<span>·</span>
						<span>Cancela quando quiser</span>
					</div>
				</div>
			</section>
		</>
	);
};

export { ApresentacaoContent };
